import type { SessionData, SessionDataFromUserInfoCookie } from "./types.js";
import { getCookieCached } from "../internal/cookieUtils.js";
import { getMetaTagContent, parseMetaTag } from "../internal/metaTagParser.js";
import ripData from "../content/regions.js";

export const SESSION_DATA_NAME_ATTRIBUTE = "awsc-session-data";
export const MEZZ_REGION_NAME_ATTRIBUTE = "awsc-mezz-region";

const sessionDataTemplate = {
  accountId: undefined,
  sessionARN: undefined,
  sessionId: undefined,
  sessionDifferentiator: undefined,
  identityToken: undefined,
  accountAlias: undefined,
  displayName: undefined,
  infrastructureRegion: undefined,
  vpc: {
    vpcId: undefined,
    vpceId: undefined,
  },
  prismModeEnabled: undefined,
  dualstackEnabledDomain: undefined,
  websiteDomainDualstack: undefined,
};

type RegionKey = keyof typeof ripData.regions;

type SessionDataKey = keyof SessionData;
function validateSessionDataFields(data: SessionData | undefined, fields: SessionDataKey[]): boolean {
  if (!data) {
    throw new Error("SessionData object is null or undefined");
  }

  const missingFieldsInSessionData: SessionDataKey[] = [];
  fields.forEach((sessionDataField) => {
    if (sessionDataField === "sessionDifferentiator" && !data.prismModeEnabled) {
      return;
    }

    if (sessionDataField === "websiteDomainDualstack" && !data.dualstackEnabledDomain) {
      return;
    }

    if (data[sessionDataField] == null) {
      missingFieldsInSessionData.push(sessionDataField);
    }
  });

  // TODO: do we need to validate any other fields?
  if (missingFieldsInSessionData.length) {
    const message = JSON.stringify({ missingFieldsInSessionData });
    throw new Error(message);
  }

  return true;
}

export class SessionDataParser {
  private static sessionDataParserInstance: SessionDataParser;

  private readonly sessionDataCache: SessionData | undefined;
  protected constructor() {
    this.sessionDataCache = this.parseSessionDataMetaTag();
  }

  /*
   * Return SessionDataProvider instance for singleton class.
   */
  public static getInstance(): SessionDataParser {
    if (!this.sessionDataParserInstance) {
      this.sessionDataParserInstance = new SessionDataParser();
    }
    return this.sessionDataParserInstance;
  }

  /**
   * Validate an object to see if it contains the required keys for session data
   *
   * @param {any} data - an object that needs to be validated
   * @throws
   * @returns void
   */
  public static validateSessionData(data?: SessionData) {
    validateSessionDataFields(data, [
      "sessionARN",
      "identityToken",
      "accountId",
      "infrastructureRegion",
      "sessionDifferentiator",
      "dualstackEnabledDomain",
      "websiteDomainDualstack",
    ]);
  }

  /**
   * Validate the session data in {@link SessionDataParser}
   *
   * @throws
   * @returns void
   */
  public validateSessionData() {
    SessionDataParser.validateSessionData(this.sessionDataCache);
  }

  public getSessionData(): SessionData {
    if (this.sessionDataCache) {
      return this.sessionDataCache;
    }
    return this.parseSessionDataMetaTag();
  }

  private parseSessionDataMetaTag(): SessionData {
    let sessionData: SessionData | undefined;
    let legacySessionData: SessionData | undefined;

    try {
      // parse session data from metatag
      sessionData = parseMetaTag(SESSION_DATA_NAME_ATTRIBUTE);
      // Validate session data, if invalid - parse session data from legacy
      if (
        !validateSessionDataFields(sessionData, [
          "sessionARN",
          "identityToken",
          "accountId",
          "infrastructureRegion",
          "sessionDifferentiator",
        ])
      ) {
        legacySessionData = this.parseSessionDataLegacy();
      }
    } catch (error) {
      console.log(error);
      try {
        // error in parsing session data meta tag, falling back to legacy
        legacySessionData = this.parseSessionDataLegacy();
      } catch (e) {
        // Ignore the error, legacySessionData will be undefined
      }
    }

    const region = sessionData?.infrastructureRegion || legacySessionData?.infrastructureRegion;

    return {
      ...sessionDataTemplate, // ensure that all fields from session data template are returned, even if undefined
      // resolve missing session data fields (if any) from legacy session data
      ...this.getDualstackDomainData(region),
      ...legacySessionData,
      ...sessionData,
    };
  }

  private decryptUserInfoSignedCookie(userinfoSignedValue: string) {
    try {
      const encodedUserInfoValue = userinfoSignedValue.split(".")[1];
      const decodedUserInfoValue = atob(encodedUserInfoValue);
      return JSON.parse(decodedUserInfoValue);
    } catch (e) {
      console.warn("Unable to decrypt userInfoSigned cookie");
      return "";
    }
  }

  private getSessionDataFromUserInfoCookie(): SessionDataFromUserInfoCookie {
    const cookieValue = getCookieCached("aws-userInfo");
    const cookieValueUserinfoSigned = getCookieCached("aws-userInfo-signed");
    const ARN_REGEX = /arn:([^:]*):([^:]*):([^:]*):([^:]*):([^:]*)/;

    if (cookieValue && cookieValueUserinfoSigned) {
      const { arn, alias, username } = JSON.parse(cookieValue);
      const match = ARN_REGEX.exec(arn);

      return {
        accountId: match ? match[4] : "",
        sessionARN: arn,
        accountAlias: alias,
        displayName: username,
        identityToken: cookieValueUserinfoSigned,
        sessionDifferentiator: this.decryptUserInfoSignedCookie(cookieValueUserinfoSigned)?.sessionDiff ?? undefined,
      };
    }
    throw new Error("No userinfo or userinfo-signed cookie found!");
  }

  private getInfrastructureRegionFromMezzanineMetaTag(): string | undefined {
    return getMetaTagContent(MEZZ_REGION_NAME_ATTRIBUTE);
  }

  private parseSessionDataLegacy(): SessionData {
    try {
      return {
        ...this.getSessionDataFromUserInfoCookie(),
        infrastructureRegion: this.getInfrastructureRegionFromMezzanineMetaTag(),
      } as SessionData;
    } catch (error) {
      console.warn("Unable to parse the data using legacy methods");
      throw error;
    }
  }

  private getDualstackDomainData(region: string | undefined): {
    dualstackEnabledDomain: boolean | undefined;
    websiteDomainDualstack: string | undefined;
  } {
    const ultimateFallbackData = {
      dualstackEnabledDomain: undefined,
      websiteDomainDualstack: undefined,
    };

    if (!region || !Object.keys(ripData.regions).includes(region)) {
      console.error("Couldn't determine console domain and dual-stack values.");
      return ultimateFallbackData;
    }

    const ripRegion = ripData.regions[region as RegionKey];

    const { websiteDomain, websiteDomainDualstack } = ripRegion;
    const { hostname } = window.location;

    if (!websiteDomainDualstack) {
      return {
        dualstackEnabledDomain: false,
        websiteDomainDualstack: websiteDomain,
      };
    }

    let dualstackEnabledDomain: boolean | undefined;
    if (hostname.endsWith(`.${websiteDomainDualstack}`)) {
      dualstackEnabledDomain = true;
    } else if (hostname.endsWith(`.${websiteDomain}`)) {
      dualstackEnabledDomain = false;
    }

    return {
      dualstackEnabledDomain,
      websiteDomainDualstack: dualstackEnabledDomain ? websiteDomainDualstack : undefined,
    };
  }
}
